import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from '../../Button/Button';
import {
  generateContent,
  regenerateContent,
  retrieveGeneratedContent
} from '../../../service/ContentGeneration';
import { sanitize } from '../../../utils/HtmlSanitizer';
import { StringToJSX } from '../../../utils/HtmlParser';
import Alert from '../../Alert/Alert';
import { ReactComponent as IconContentCopy } from '../../../public/images/svg/icon_content_copy.svg';
import { ReactComponent as IconFixHeight } from '../../../public/images/svg/icon_auto_fix_high_sm.svg';
import Loading from '../../Loading/Loading';
import { COPY_ANIMATION_DURATION } from '../../../constants';
import useErrorProneRequest from '../../../hooks/useErrorProneRequest';
import { createCustomError } from '../../../utils/createCustomError';

const SummaryPanel = ({ content = '', contentRequestId, onChange }) => {
  const [generateError, generateInProgress, generateRequest] = useErrorProneRequest();
  const [fetchError, isFetching, fetchRequest] = useErrorProneRequest();
  const [isLocked, setIsLocked] = useState(false);
  const [summary, setSummary] = useState(null);
  const [hasCopiedContent, setCopiedContent] = useState(false);

  const hasSummary = !!summary;
  const error = generateError ?? fetchError;
  const hasError = error !== null;
  const isLoading = generateInProgress || isFetching;
  const generateDisabled = isLoading || isLocked;
  const copyDisabled = isLoading || isLocked || hasError || !hasSummary;

  const handleCopy = async (text) => {
    await navigator.clipboard
      .writeText(text)
      .then(
        () => {
          setCopiedContent(true);
        },
        () => {
          console.error('Failed to copy');
        }
      )
      .finally(() => {
        setTimeout(() => {
          setCopiedContent(false);
        }, COPY_ANIMATION_DURATION);
      });
  };

  const handleSummaryResponse = (response = {}) => {
    if (response?.output) {
      setSummary({
        output: sanitize(response?.output).replace(/\n\n/g, '\n'),
        contentRequestId: response.contentRequestId,
        requestTryNumber: response.requestTryNumber
      });
    }

    setIsLocked(response.requestTryNumber !== response.lastRequestTryNumber);

    if (response?.error) {
      throw createCustomError(response?.output, response.error);
    }
  };

  const getSummary = useCallback(
    (contentRequestId, requestTryNumber) =>
      fetchRequest(async () => {
        const response = await retrieveGeneratedContent({
          contentRequestId,
          lastRequestTryNumber: requestTryNumber ?? 'latest'
        });

        handleSummaryResponse(response);
      }),
    [fetchRequest]
  );

  const generateSummary = useCallback(
    () =>
      generateRequest(async () => {
        const { contentRequestId, lastRequestTryNumber } = await generateContent({
          content,
          contentType: 'summary'
        });
        onChange?.({ contentRequestId, lastRequestTryNumber });
        await getSummary(contentRequestId, lastRequestTryNumber);
      }),
    [generateRequest, content, onChange, getSummary]
  );

  const regenerateSummary = () =>
    generateRequest(async () => {
      const { contentRequestId, lastRequestTryNumber } = await regenerateContent({
        content,
        contentType: 'summary',
        contentRequestId: summary.contentRequestId,
        lastRequestTryNumber: summary.requestTryNumber
      });
      onChange?.({ contentRequestId, lastRequestTryNumber });
      await getSummary(contentRequestId, lastRequestTryNumber);
    });

  const retrySummary = () => {
    return summary?.contentRequestId && summary?.requestTryNumber
      ? regenerateSummary()
      : generateSummary();
  };

  useEffect(() => {
    contentRequestId ? getSummary(contentRequestId) : generateSummary();
  }, []);

  return (
    <div className="summary-panel">
      <div className="flex items-center gap-4">
        <div className="flex-grow">
          <h3 className="text-xl font-normal leading-5 py-2.5 my-4">Summary</h3>
        </div>
        {hasError ? (
          <div>
            <Button
              disabled={generateDisabled}
              onClick={retrySummary}
              label="Retry"
              name="retry-summary"
              dataTest="retry-summary-button"
              type="button"
              className="bg-refresh-button-100 border border-solid border-purple-100 rounded-lg px-2 py-1 text-sm text-purple-200 font-semibold flex gap-2 justify-center items-center disabled:cursor-default disabled:opacity-50"
            />
          </div>
        ) : hasSummary ? (
          <>
            <div>
              <Button
                disabled={generateDisabled}
                onClick={regenerateSummary}
                label="Regenerate"
                name="generate-summary"
                dataTest="generate-summary-button"
                type="button"
                className="bg-refresh-button-100 border border-solid border-purple-100 rounded-lg px-2 py-1 text-sm text-purple-200 font-semibold flex gap-2 justify-center items-center disabled:cursor-default disabled:opacity-50"
                icon={<IconFixHeight />}
              />
            </div>
            <div>
              <Button
                disabled={copyDisabled}
                onClick={() => handleCopy(summary?.output)}
                label={hasCopiedContent ? 'Copied!' : 'Copy'}
                name="copy-summary"
                dataTest="copy-summary-button"
                type="button"
                className={[
                  'transition ease-linear min-w-[90px] text-sm font-semibold text-grey-400 flex gap-2 justify-center items-center p-2 rounded disabled:cursor-default disabled:opacity-50',
                  ...(hasCopiedContent ? ['bg-green-400'] : ['bg-transparent '])
                ].join(' ')}
                icon={<IconContentCopy />}
              />
            </div>
          </>
        ) : null}
      </div>

      <div className="pb-4 mb-4 text-sm text-grey-400" data-testid="summary-panel-content">
        {isLoading ? (
          <div className="h-52 overflow-auto w-full flex flex-col justify-center items-center">
            <div className="h-16 w-16">
              <Loading />
            </div>
            <div className="text-sm font-bold text-grey-400">Generating Summary</div>
          </div>
        ) : hasError ? (
          <Alert level="error" label={error.label}>
            {error.details}
          </Alert>
        ) : summary?.output ? (
          <StringToJSX domString={summary?.output} />
        ) : null}
      </div>
    </div>
  );
};

SummaryPanel.propTypes = {
  content: PropTypes.string.isRequired,
  contentRequestId: PropTypes.string,
  onChange: PropTypes.func
};

export default SummaryPanel;
